import {
  useCreatePatientReportMutation,
  useCreateRecordReportMutation,
  useCreateTaskReportMutation,
  useCreateUserReportMutation,
  useEditPatientReportMutation,
  useEditRecordReportMutation,
  useEditTaskReportMutation,
  useEditUserReportMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useNavigate } from 'react-router-dom';
import { useReportStore } from '@store';
import { toast } from 'react-toastify';
import { IPatientReport, ITaskReport } from '@common/types';
import { useIsEdit } from './index.ts';

export const useHandleSaveReport = () => {
  const navigate = useNavigate();
  const { report, filters, setReport } = useReportStore();
  const isEdit = useIsEdit();

  const createPatientReport = useCreatePatientReportMutation();
  const editPatientReport = useEditPatientReportMutation(report?.id ?? '');

  const createUserReport = useCreateUserReportMutation();
  const editUserReport = useEditUserReportMutation(report?.id ?? '');

  const createTaskReport = useCreateTaskReportMutation();
  const editTaskReport = useEditTaskReportMutation(report?.id ?? '');

  const createRecordReport = useCreateRecordReportMutation();
  const editRecordReport = useEditRecordReportMutation(report?.id ?? '');

  const handleSaveReport = async () => {
    const sanitizedCustomTimeframe =
      report?.custom_timeframe && report?.interval === 'custom'
        ? {
            ...(report.custom_timeframe.from_date
              ? { from_date: report.custom_timeframe.from_date }
              : {}),
            ...(report.custom_timeframe.to_date
              ? { to_date: report.custom_timeframe.to_date }
              : {}),
          }
        : undefined;

    const sanitizedSelect = Array.from(
      new Set((report?.options?.select ?? []).filter(Boolean)),
    );

    const sanitizedFilters = filters?.filter(
      expr =>
        expr?.value !== undefined && expr?.value !== '' && expr?.value !== null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any;

    const payload = {
      name: report?.name ?? 'Untitled Report',
      description: report?.description ?? '',
      report_folder_id: report?.report_folder?.id ?? '',
      private: report?.private ?? false,
      allow_others_to_edit: report?.allow_others_to_edit ?? false,
      interval: report?.interval ?? 'all_time',
      ...(sanitizedCustomTimeframe && {
        custom_timeframe: sanitizedCustomTimeframe,
      }),
      options: {
        ...(report?.report_type === 'patients_report' && {
          patient_report_type:
            (report as IPatientReport)?.options.patient_report_type ??
            'all_patients',
        }),
        ...(report?.report_type === 'tasks_report' && {
          include_future_tasks:
            (report as ITaskReport)?.options.include_future_tasks ?? false,
        }),
        ...(report?.report_type === 'records_report' && {
          record_report_type: 'all',
        }),
        select: sanitizedSelect,
        ...(sanitizedFilters?.length
          ? {
              $and: sanitizedFilters,
            }
          : {}),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mutations: Record<string, any> = {
      patients_report: isEdit ? editPatientReport : createPatientReport,
      tasks_report: isEdit ? editTaskReport : createTaskReport,
      records_report: isEdit ? editRecordReport : createRecordReport,
      users_report: isEdit ? editUserReport : createUserReport,
    };

    const mutation = mutations[report?.report_type ?? ''];

    if (!mutation) return;
    try {
      const response = await mutation.mutateAsync(payload);

      const reportId = isEdit ? report?.id : response.data.id;
      if (!isEdit) setReport({ id: reportId ?? '' });

      navigate(`/reporting/items/${reportId}`);
      toast.success('Report successfully saved');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const isPending = [
    createPatientReport.isPending,
    editPatientReport.isPending,
    createUserReport.isPending,
    editUserReport.isPending,
    createTaskReport.isPending,
    editTaskReport.isPending,
    createRecordReport.isPending,
    editRecordReport.isPending,
  ].some(isPending => isPending);

  return { handleSaveReport, isPending };
};
